import React, { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { COLORS, FONTS } from '../theme';

/**
 * Duel UI surface — three overlays, one file (they share data flow).
 *
 *   - DuelChallengePopup: modal when an incoming /duel challenge arrives.
 *     "Accept / Decline" buttons. Auto-dismiss on 30s timeout (server
 *     also expires; this is just visual).
 *   - DuelHUD: top-banner during ACTIVE duel showing both names + HP.
 *   - DuelEndOverlay: brief "Victory! / Defeat / Forfeit / Draw" toast
 *     after duelEnded; auto-dismiss after 3s.
 *
 * All three subscribe to room messages (duelChallenge, duelCountdownStarted,
 * duelStarted, duelEnded) via the polling-bind pattern used by DeathOverlay
 * (re-binds the room every 500ms to survive reconnects). Per-message
 * filtering ensures we only show UI for THIS client's duels.
 *
 * Auto-capture (per feedback_automated_capture_phase_1.md): each overlay
 * has a stable data-testid so the Playwright driver can assert presence
 * + capture screenshots without scraping innerText.
 */

function getMySid(): string | null {
  return (globalThis as any).nm?.getRoom?.()?.sessionId ?? null;
}
const END_OVERLAY_DURATION_MS = 3000;

interface IncomingChallenge {
  duelId: string;
  challengerSid: string;
  challengerName: string;
  targetSid: string;
  targetName: string;
  expiresAtMs: number;
}

interface ActiveDuel {
  duelId: string;
  challengerSid: string;
  challengerName: string;
  targetSid: string;
  targetName: string;
  countdownEndAtMs?: number; // present during COUNTDOWN
  startedAtMs?: number;       // present during ACTIVE
}

interface EndedDuel {
  duelId: string;
  outcome: 'victory' | 'defeat' | 'draw' | 'forfeit-mine' | 'forfeit-other';
  winnerName: string;
  reason: string;
  shownAtMs: number;
}

export const DuelOverlays: React.FC = () => {
  const [challenge, setChallenge] = useState<IncomingChallenge | null>(null);
  const [active, setActive] = useState<ActiveDuel | null>(null);
  const [ended, setEnded] = useState<EndedDuel | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  useColyseusMessages({
    // Incoming challenge — only show when *I* am the target
    duelChallenge: (msg: any) => {
      const mySid = getMySid();
      if (msg?.targetSid !== mySid) return;
      setChallenge({
        duelId: msg.duelId,
        challengerSid: msg.challengerSid,
        challengerName: msg.challengerName,
        targetSid: msg.targetSid,
        targetName: msg.targetName,
        expiresAtMs: Date.now() + (msg.timeoutMs ?? 30_000),
      });
    },
    // Countdown — both parties see it; clears the popup if I'm the target
    duelCountdownStarted: (msg: any) => {
      const mySid = getMySid();
      if (msg?.challengerSid !== mySid && msg?.targetSid !== mySid) return;
      setChallenge(null);
      setActive({
        duelId: msg.duelId,
        challengerSid: msg.challengerSid,
        challengerName: msg.challengerName,
        targetSid: msg.targetSid,
        targetName: msg.targetName,
        countdownEndAtMs: Date.now() + (msg.countdownMs ?? 3000),
      });
    },
    // ACTIVE — both parties' HUD shows full duel info
    duelStarted: (msg: any) => {
      const mySid = getMySid();
      if (msg?.challengerSid !== mySid && msg?.targetSid !== mySid) return;
      setActive({
        duelId: msg.duelId,
        challengerSid: msg.challengerSid,
        challengerName: msg.challengerName,
        targetSid: msg.targetSid,
        targetName: msg.targetName,
        startedAtMs: Date.now(),
      });
    },
    // End — determine outcome from my POV
    duelEnded: (msg: any) => {
      const mySid = getMySid();
      if (msg?.challengerSid !== mySid && msg?.targetSid !== mySid) return;
      setActive(null);
      setChallenge(null);

      let outcome: EndedDuel['outcome'] = 'draw';
      if (msg.reason === 'forfeit') {
        outcome = msg.loserSid === mySid ? 'forfeit-mine' : 'forfeit-other';
      } else if (msg.reason === 'decline') {
        outcome = 'draw';
      } else if (msg.reason === 'distance' || msg.reason === 'timeout') {
        outcome = 'draw';
      } else if (msg.winnerSid === mySid) {
        outcome = 'victory';
      } else if (msg.winnerSid && msg.winnerSid !== mySid) {
        outcome = 'defeat';
      }

      setEnded({
        duelId: msg.duelId,
        outcome,
        winnerName: msg.winnerName ?? '',
        reason: msg.reason ?? 'ended',
        shownAtMs: Date.now(),
      });
    },
  });

  // Countdown ticker — runs only while a challenge or a duel shows (the two
  // countdowns that read `now`). B7: an always-on 10 Hz ticker re-rendered
  // this hidden component all game long.
  const ticking = challenge !== null || active !== null;
  useEffect(() => {
    if (!ticking) return undefined;
    setNow(Date.now());
    const tickInterval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(tickInterval);
  }, [ticking]);

  // Auto-dismiss the end overlay after END_OVERLAY_DURATION_MS
  useEffect(() => {
    if (!ended) return;
    const remaining = (ended.shownAtMs + END_OVERLAY_DURATION_MS) - Date.now();
    if (remaining <= 0) { setEnded(null); return; }
    const t = setTimeout(() => setEnded(null), remaining);
    return () => clearTimeout(t);
  }, [ended]);

  // Auto-dismiss the challenge popup if it expires (server also enforces)
  useEffect(() => {
    if (!challenge) return;
    const remaining = challenge.expiresAtMs - Date.now();
    if (remaining <= 0) { setChallenge(null); return; }
    const t = setTimeout(() => setChallenge(null), remaining);
    return () => clearTimeout(t);
  }, [challenge]);

  return (
    <>
      {challenge && (
        <ChallengePopup
          challenge={challenge}
          remainingMs={Math.max(0, challenge.expiresAtMs - now)}
          onAccept={() => sendChat(`/duel accept`)}
          onDecline={() => sendChat(`/duel decline`)}
        />
      )}
      {active && (
        <DuelHUD
          active={active}
          countdownRemainingMs={active.countdownEndAtMs ? Math.max(0, active.countdownEndAtMs - now) : 0}
        />
      )}
      {ended && <EndOverlay ended={ended} />}
    </>
  );
};

function sendChat(message: string) {
  const room = (window as any).nm?.getRoom?.();
  if (room) room.send('chatMessage', { message });
}

const ChallengePopup: React.FC<{
  challenge: IncomingChallenge;
  remainingMs: number;
  onAccept: () => void;
  onDecline: () => void;
}> = ({ challenge, remainingMs, onAccept, onDecline }) => (
  <div
    data-testid="duel-challenge-popup"
    data-duel-id={challenge.duelId}
    style={{
      position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(20, 10, 30, 0.95)', border: `2px solid ${COLORS.warning}`,
      borderRadius: 10, padding: '20px 24px', minWidth: 360, zIndex: 9998,
      color: COLORS.textPrimary, fontFamily: 'Inter, sans-serif',
      boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    }}
  >
    <div style={{ fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, marginBottom: 8, color: COLORS.warning }}>
      ⚔️ Duel Challenge
    </div>
    <div style={{ fontSize: 16, marginBottom: 18 }}>
      <span style={{ color: COLORS.danger, fontWeight: 600 }}>{challenge.challengerName}</span>
      <span style={{ opacity: 0.85 }}> challenges you to a 1v1.</span>
    </div>
    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
      <button
        data-testid="duel-decline-button"
        onClick={onDecline}
        style={btnStyle('#7f8c8d')}
      >
        Decline
      </button>
      <button
        data-testid="duel-accept-button"
        onClick={onAccept}
        style={btnStyle('#27ae60')}
      >
        Accept
      </button>
    </div>
    <div style={{ marginTop: 12, fontSize: 12, opacity: 0.6, textAlign: 'right' }}>
      Expires in {Math.ceil(remainingMs / 1000)}s
    </div>
  </div>
);

const DuelHUD: React.FC<{
  active: ActiveDuel;
  countdownRemainingMs: number;
}> = ({ active, countdownRemainingMs }) => (
  <div
    data-testid="duel-hud"
    data-duel-id={active.duelId}
    style={{
      position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(15, 8, 20, 0.85)', border: `1px solid ${COLORS.warning}`,
      borderRadius: 8, padding: '8px 16px', zIndex: 9997,
      color: COLORS.textPrimary, fontFamily: 'Inter, sans-serif', fontSize: 14,
      display: 'flex', alignItems: 'center', gap: 10,
    }}
  >
    <span style={{ fontWeight: 700, color: COLORS.warning }}>⚔️ DUEL</span>
    <span style={{ color: COLORS.danger, fontWeight: 600 }}>{active.challengerName}</span>
    <span style={{ opacity: 0.7 }}>vs</span>
    <span style={{ color: COLORS.info, fontWeight: 600 }}>{active.targetName}</span>
    {countdownRemainingMs > 0 && (
      <span data-testid="duel-countdown" style={{ marginLeft: 12, color: COLORS.warning, fontWeight: 700 }}>
        Starting in {Math.ceil(countdownRemainingMs / 1000)}…
      </span>
    )}
  </div>
);

const EndOverlay: React.FC<{ ended: EndedDuel }> = ({ ended }) => {
  const { color, label } = endVisualFor(ended.outcome);
  return (
    <div
      data-testid="duel-end-overlay"
      data-duel-outcome={ended.outcome}
      style={{
        position: 'fixed', top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'rgba(10, 5, 15, 0.85)', border: `2px solid ${color}`,
        borderRadius: 12, padding: '24px 36px', zIndex: 9999,
        color: COLORS.textPrimary, fontFamily: 'Inter, sans-serif', textAlign: 'center',
        boxShadow: '0 10px 30px rgba(0,0,0,0.7)',
      }}
    >
      <div style={{ fontFamily: FONTS.display, fontSize: 32, fontWeight: 700, color, letterSpacing: '0.05em' }}>
        {label}
      </div>
      {ended.winnerName && (
        <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>
          {ended.outcome === 'victory' ? 'You won!' :
            ended.outcome === 'defeat' ? `${ended.winnerName} won.` : ''}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.5 }}>
        End reason: {ended.reason}
      </div>
    </div>
  );
};

function endVisualFor(outcome: EndedDuel['outcome']): { color: string; label: string } {
  switch (outcome) {
    case 'victory':       return { color: COLORS.success, label: '🏆 VICTORY' };
    case 'defeat':        return { color: COLORS.danger, label: '💀 DEFEAT' };
    case 'forfeit-mine':  return { color: COLORS.warning, label: '🏳️ FORFEIT' };
    case 'forfeit-other': return { color: COLORS.success, label: '🏆 OPPONENT FORFEIT' };
    case 'draw':
    default:              return { color: COLORS.textMuted, label: '🤝 DRAW' };
  }
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    background: bg, color: COLORS.textPrimary, border: 'none',
    borderRadius: 6, padding: '10px 20px', fontWeight: 600,
    fontSize: 14, cursor: 'pointer',
  };
}
